/**
 * Delivery window helpers.
 *
 * A window carries hours (start/end, legacy) and, when set by a dispatcher, exact minutes from midnight
 * (startMinutes/endMinutes). Always read times through getWindowMinutes so both shapes work.
 * Only the standard windows (Morning/Midday/Evening) carry a stored label (location presets and custom windows
 * have none), but it is never shown: every screen shows the time range.
 */

export type WindowLike = {
  start: number;
  end: number;
  label?: string;
  startMinutes?: number;
  endMinutes?: number;
};

export type WindowMinutes = { startMinutes: number; endMinutes: number };

export type DeliveryWindowPresetGroup = "standard" | "location";

export type DeliveryWindowPreset = WindowLike & {
  startMinutes: number;
  endMinutes: number;
  label: string;
  group: DeliveryWindowPresetGroup;
};

/** First and last selectable times for Time Required and custom windows (9:00 AM - 8:00 PM). */
export const TIME_SLOT_START_MINUTES = 9 * 60;
export const TIME_SLOT_END_MINUTES = 20 * 60;
/** Custom window From/To times are offered every 15 minutes. */
export const WINDOW_TIME_STEP_MINUTES = 15;
/** Time Required is offered every 30 minutes. */
export const TIME_REQUIRED_STEP_MINUTES = 30;

export function getWindowMinutes(window?: WindowLike | null): WindowMinutes | null {
  if (!window) return null;
  const startMinutes = window.startMinutes ?? window.start * 60;
  const endMinutes = window.endMinutes ?? window.end * 60;
  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || startMinutes >= endMinutes) return null;
  return { startMinutes, endMinutes };
}

/** Stable key for comparing windows, e.g. "570-870". */
export function getWindowKey(window?: WindowLike | null): string {
  const m = getWindowMinutes(window);
  return m ? `${m.startMinutes}-${m.endMinutes}` : "";
}

export function isSameWindow(a?: WindowLike | null, b?: WindowLike | null): boolean {
  const key = getWindowKey(a);
  return key !== "" && key === getWindowKey(b);
}

/** 570 -> "9:30 AM" */
export function formatMinutesAsTime(totalMinutes: number): string {
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const period = hours24 < 12 ? "AM" : "PM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(minutes).padStart(2, "0")} ${period}`;
}

/** 570 -> "09:30" (value format used by Time Required) */
export function minutesToHHmm(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  return `${String(hours).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
}

/** "09:30" -> 570; null when malformed */
export function hhmmToMinutes(value?: string | null): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec((value ?? "").trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** "9:30 AM - 2:30 PM" */
export function formatWindowRange(window?: WindowLike | null): string {
  const m = getWindowMinutes(window);
  return m ? `${formatMinutesAsTime(m.startMinutes)} - ${formatMinutesAsTime(m.endMinutes)}` : "";
}

/** Window text shown to people: always the time range; Morning/Midday/Evening names are never shown. */
export function formatWindowName(window?: WindowLike | null): string {
  return formatWindowRange(window);
}

// Old standard window names saved as text, e.g. "Evening (5pm - 9pm)" or "Morning (9am - 1pm)"
const NAMED_WINDOW_TEXT = /^(?:morning|midday|evening)\s*\((.+)\)$/i;
const TIME_OF_DAY_TEXT = /(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?/gi;

/**
 * Replaces an old named window text ("Evening (5pm - 9pm)") with its time range ("5:00 PM - 9:00 PM").
 * Only text that is exactly a window name is changed; any other comment is returned as is.
 */
export function replaceNamedWindowText(text?: string | null): string {
  const named = NAMED_WINDOW_TEXT.exec(text?.trim() ?? "");
  if (!named) return text ?? "";

  const times = [...named[1].matchAll(TIME_OF_DAY_TEXT)].map((m) => {
    const hours12 = Number(m[1]) % 12;
    const minutes = Number(m[2] ?? 0);
    return (m[3].toLowerCase() === "p" ? hours12 + 12 : hours12) * 60 + minutes;
  });
  if (times.length === 2 && times[0] < times[1]) {
    return `${formatMinutesAsTime(times[0])} - ${formatMinutesAsTime(times[1])}`;
  }
  return named[1].trim();
}

/** Time options every stepMinutes from 9:00 AM to 8:00 PM, as { value: "HH:mm", label: "9:00 am", minutes }. */
export function getTimeSlotOptions(stepMinutes: number): Array<{ value: string; label: string; minutes: number }> {
  const options: Array<{ value: string; label: string; minutes: number }> = [];
  for (let m = TIME_SLOT_START_MINUTES; m <= TIME_SLOT_END_MINUTES; m += stepMinutes) {
    options.push({ value: minutesToHHmm(m), label: formatMinutesAsTime(m).toLowerCase(), minutes: m });
  }
  return options;
}

/** Finds the preset matching a window's times, if any. */
export function findMatchingPreset(
  presets: readonly DeliveryWindowPreset[],
  window?: WindowLike | null
): DeliveryWindowPreset | undefined {
  return presets.find((p) => isSameWindow(p, window));
}

/**
 * Window filter values from the Autocall API are either a named label ("Morning (9am - 1pm)")
 * or a range key for unnamed windows ("09:30-14:30"). Formats range keys for display.
 */
export function formatWindowFilterValue(value: string): string {
  const match = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/.exec((value ?? "").trim());
  if (!match) return value;
  const start = Number(match[1]) * 60 + Number(match[2]);
  const end = Number(match[3]) * 60 + Number(match[4]);
  return `${formatMinutesAsTime(start)} - ${formatMinutesAsTime(end)}`;
}
